import { deleteStudyAuthoring, readStudyAuthoringDeletionStatus } from '../../hooks/study-builder/studyAuthoringClient'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import type { StudyAuthoringDeletionStatusResponse } from '../../hooks/study-builder/studyAuthoringClient'
import { trackShopifyAction } from '../../shopifyUiTracking'
import { shopifyComponentCopy } from '@testfeed/product-language'

export type StudyDeletionOutcome =
  | { status: 'completed'; receipt: StudyAuthoringDeletionStatusResponse }
  | { status: 'pending'; operationId: string; receipt?: StudyAuthoringDeletionStatusResponse }

export async function runStudyAuthoringDeletion({
  shopify,
  studyId,
  operationId: existingOperationId,
  retryFailed = false,
  waitForCompletion = true,
  signal,
  isActive,
  onReceipt,
  onOperationId,
}: {
  shopify: ShopifyAppBridge
  studyId: string
  operationId?: string | null
  waitForCompletion?: boolean
  retryFailed?: boolean
  signal?: AbortSignal
  isActive?: () => boolean
  onReceipt?: (receipt: StudyAuthoringDeletionStatusResponse) => void
  onOperationId?: (operationId: string) => void
}): Promise<StudyDeletionOutcome> {
  const assertActive = () => {
    signal?.throwIfAborted()
    if (isActive && !isActive()) throw new DOMException('Deletion visit ended', 'AbortError')
  }
  assertActive()
  let operationId = existingOperationId ?? null
  if (operationId) {
    const recoveredOperationId = operationId
    const recovered = await trackShopifyAction(shopify, 'check-deletion', () =>
      readStudyAuthoringDeletionStatus(shopify, { studyId, operationId: recoveredOperationId }, signal),
    )
    assertActive()
    onReceipt?.(recovered)
    if (recovered.status === 'completed') return { status: 'completed', receipt: recovered }
    if (recovered.status === 'failed') {
      if (!retryFailed)
        throw Object.assign(new Error(shopifyComponentCopy.studySetupBulkDeleteDialog.previousFailure), {
          receipt: recovered,
        })
      operationId = null
    }
  }
  if (!operationId) {
    assertActive()
    operationId = (
      await trackShopifyAction(shopify, 'request-deletion', () => deleteStudyAuthoring(shopify, studyId, signal))
    ).operationId
    assertActive()
    onOperationId?.(operationId)
  }
  if (!waitForCompletion) return { status: 'pending', operationId }
  let latest: StudyAuthoringDeletionStatusResponse | undefined
  for (let attempt = 0; attempt < 30; attempt++) {
    assertActive()
    latest = await trackShopifyAction(shopify, 'check-deletion', () =>
      readStudyAuthoringDeletionStatus(shopify, { studyId, operationId }, signal),
    )
    assertActive()
    onReceipt?.(latest)
    if (latest.status === 'completed') return { status: 'completed', receipt: latest }
    if (latest.status === 'failed')
      throw Object.assign(new Error(shopifyComponentCopy.studySetupBulkDeleteDialog.deletionFailure), {
        receipt: latest,
      })
    await new Promise<void>((resolve, reject) => {
      const finish = () => {
        signal?.removeEventListener('abort', onAbort)
        resolve()
      }
      const onAbort = () => {
        clearTimeout(timeout)
        signal?.removeEventListener('abort', onAbort)
        reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'))
      }
      const timeout = setTimeout(finish, 1000)
      if (signal?.aborted) onAbort()
      else signal?.addEventListener('abort', onAbort, { once: true })
    })
  }
  if (!latest) throw new Error(shopifyComponentCopy.studySetupBulkDeleteDialog.statusUnavailable)
  return { status: 'pending', operationId, receipt: latest }
}
